import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiHealthTrackerService } from '../health-tracker.service';
import {
  SleepDayResponse,
  SleepRangeResponse,
  SleepRequestParamsDay,
  SleepRequestParamsRange
} from './sleep.interface';

/**
 * @description Sleep Service.
 * @exports
 * @class ApiHealthTrackerSleepService
 */
@Injectable()
export class ApiHealthTrackerSleepService {
  private readonly apiHealthTrackerService = inject(ApiHealthTrackerService);

  /**
   * Fetches sleep data for a specific day.
   *
   * @param {SleepRequestParamsDay} params - The parameters for the request.
   * @returns {Observable<SleepDayResponse>} An observable containing the sleep data for the specified day.
   */
  fetchDayData(params: SleepRequestParamsDay): Observable<SleepDayResponse> {
    return this.apiHealthTrackerService
      .get<SleepDayResponse>('/sleep/day', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }
  /**
   * Fetches sleep data for a specific month.
   *
   * @param {SleepRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<SleepRangeResponse>} An observable containing the sleep data for the specified month.
   */
  fetchMonthData(
    params: SleepRequestParamsRange
  ): Observable<SleepRangeResponse> {
    return this.apiHealthTrackerService
      .get<SleepRangeResponse>('/sleep/month', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }

  /**
   * Fetches sleep data for a specific week.
   *
   * @param {SleepRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<SleepRangeResponse>} An observable containing the sleep data for the specified week.
   */
  fetchWeekData(
    params: SleepRequestParamsRange
  ): Observable<SleepRangeResponse> {
    return this.apiHealthTrackerService.get<SleepRangeResponse>('/sleep/week', {
      params: {
        ...params
      }
    });
  }

  /**
   * Fetches sleep data for a specific year.
   *
   * @param {SleepRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<SleepRangeResponse>} An observable containing the sleep data for the specified year.
   */
  fetchYearData(
    params: SleepRequestParamsRange
  ): Observable<SleepRangeResponse> {
    return this.apiHealthTrackerService
      .get<SleepRangeResponse>('/sleep/year', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }
}
